import React, { useState, useEffect, useCallback, useRef, createContext, useContext, ReactNode } from 'react';
import { oktaAuth, logout as oktaLogout, restoreSession, redirectToLogin, withTimeout } from '../lib/okta/oktaConfig';
import { useCurrentUser } from '../hooks/use-current-user';
import { UserInfo, UserRole } from '../types/user';
import { apiClient } from '../services/api';

interface OktaUserInfo {
  email: string;
  name: string;
  given_name?: string;
  family_name?: string;
  [key: string]: any;
}

interface AuthContextType {
  isAuthenticated: boolean;
  isLoading: boolean;
  userInfo: UserInfo | null;
  oktaUserInfo: OktaUserInfo | null;
  error: string | null;
  login: () => Promise<void>;
  logout: () => Promise<void>;
  handleLoginRedirect: () => Promise<void>;
  hasRole: (roles: UserRole[]) => boolean;
  checkIsUserInRole: (roles: UserRole[]) => boolean;
  checkHasPermission: (permissions: string[]) => boolean;
  checkAccess: (requiredRoles?: UserRole[], requiredPermissions?: string[]) => boolean;
  isAdmin: boolean;
  isAdminUfficioStile: boolean;
  isAdminSviluppo: boolean;
  canManageRequests: boolean;
  canManageTasks: boolean;
}

const AuthContext = createContext<AuthContextType | null>(null);

export const OktaAuthProvider = ({ children }: { children: ReactNode }) => {
  const [isAuthenticated, setIsAuthenticated] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [oktaUserInfo, setOktaUserInfo] = useState<OktaUserInfo | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  
  const { data: userInfo, isLoading: isLoadingUser } = useCurrentUser(accessToken);

  // REDIRECT AUTOMATICO quando userInfo è disponibile
  useEffect(() => {
    const currentPath = window.location.pathname;
    
    
    // REDIRECT FORZATO: Se abbiamo userInfo e siamo su /login, vai via!
    if (userInfo && currentPath === '/login') {
      setTimeout(() => {
        window.location.replace('/dashboard');
      }, 50);
    }
  }, [userInfo, isLoadingUser]);

  // Verifica se l'utente ha uno dei ruoli specificati
  const hasRole = (roles: UserRole[]): boolean => {
    if (!userInfo || !Array.isArray(userInfo.roles) || roles.length === 0) {
      return false;
    }
    return roles.some(role => userInfo.roles.includes(role));
  };

  // Alias per compatibilità
  const checkIsUserInRole = hasRole;

  // Verifica se l'utente ha uno dei permessi specificati
  const checkHasPermission = (permissions: string[]): boolean => {
    // Per ora non abbiamo un sistema di permessi granulare
    // Ritorniamo true se l'utente è admin
    return isAdmin;
  };

  // Verifica accesso combinato (ruoli E/O permessi)
  const checkAccess = (requiredRoles?: UserRole[], requiredPermissions?: string[]): boolean => {
    if (!requiredRoles && !requiredPermissions) {
      return true; // Nessun requisito, accesso consentito
    }
    
    const hasRequiredRole = requiredRoles ? hasRole(requiredRoles) : true;
    const hasRequiredPermission = requiredPermissions ? checkHasPermission(requiredPermissions) : true;
    
    return hasRequiredRole && hasRequiredPermission;
  };

  // Ruoli specifici
  const isAdmin = hasRole([UserRole.Admin]);
  const isAdminUfficioStile = hasRole([UserRole.Admin, UserRole.AdminUfficioStile]);
  const isAdminSviluppo = hasRole([UserRole.Admin, UserRole.AdminSviluppo]);
  
  // Permessi specifici
  const canManageRequests = hasRole([
    UserRole.Admin, 
    UserRole.AdminUfficioStile, 
    UserRole.AdminSviluppo
  ]);
  
  const canManageTasks = hasRole([
    UserRole.Admin,
    UserRole.AdminUfficioStile,
    UserRole.OperatoreUfficioStile,
    UserRole.AdminSviluppo,
    UserRole.OperatoreSviluppo
  ]);

  const clearAuthState = useCallback(() => {
    setIsAuthenticated(false);
    setOktaUserInfo(null);
    setAccessToken(null);
    apiClient.clearToken();
  }, []);

  // Verifica iniziale: mai attese indefinite (niente iframe, tutto con timeout)
  useEffect(() => {
    // Su /login/callback ci pensa handleLoginRedirect
    if (window.location.pathname.startsWith('/login/callback')) {
      setIsLoading(false);
      return;
    }

    const checkAuth = async () => {
      try {
        setIsLoading(true);

        if (!(await restoreSession())) {
          clearAuthState();
          return;
        }

        const token = await oktaAuth.getAccessToken();
        setAccessToken(token || null);
        if (token) {
          apiClient.setToken(token);
        }
        setIsAuthenticated(true);

        const userClaims = await withTimeout(oktaAuth.getUser(), 10000);
        setOktaUserInfo({
          email: userClaims.email ?? 'email@example.com',
          name: userClaims.name ?? 'User',
          given_name: userClaims.given_name,
          family_name: userClaims.family_name,
          ...userClaims
        });
      } catch (err) {
        console.error('Error checking auth state:', err);
        setError('Si è verificato un errore durante la verifica dell\'autenticazione');
        clearAuthState();
      } finally {
        setIsLoading(false);
      }
    };

    checkAuth();
  }, [clearAuthState]);

  // Scadenza/rinnovo token e stato auth, con cleanup
  useEffect(() => {
    const applyToken = (token: string | null | undefined) => {
      setAccessToken(token || null);
      if (token) {
        apiClient.setToken(token);
      }
    };

    const onExpired = async () => {
      if (await restoreSession()) {
        applyToken(await oktaAuth.getAccessToken());
      } else {
        clearAuthState();
        redirectToLogin();
      }
    };
    const onRenewed = async () => applyToken(await oktaAuth.getAccessToken());
    const onAuthState = (authState: { isAuthenticated?: boolean }) => {
      if (!authState.isAuthenticated) {
        clearAuthState();
      }
    };

    oktaAuth.tokenManager.on('expired', onExpired);
    oktaAuth.tokenManager.on('renewed', onRenewed);
    oktaAuth.authStateManager.subscribe(onAuthState);
    oktaAuth.start();

    return () => {
      oktaAuth.tokenManager.off('expired', onExpired);
      oktaAuth.tokenManager.off('renewed', onRenewed);
      oktaAuth.authStateManager.unsubscribe(onAuthState);
      oktaAuth.stop();
    };
  }, [clearAuthState]);

  const login = async (): Promise<void> => {
    try {
      const originalUri = window.location?.pathname === '/login' ? '/' : (window.location?.pathname || '/');
      await oktaAuth.signInWithRedirect({
        originalUri: originalUri
      });
    } catch (err) {
      console.error('Login error:', err);
      setError('Si è verificato un errore durante il login');
    }
  };

  const logout = async (): Promise<void> => {
    try {
      await oktaLogout();
      setIsAuthenticated(false);
      setOktaUserInfo(null);
      setAccessToken(null);
      
      // Rimuovi il token dall'apiClient
      apiClient.clearToken();
      
      window.location.href = '/login';
    } catch (err) {
      console.error('Logout error:', err);
      setError('Si è verificato un errore durante il logout');
    }
  };

  // Il callback può essere completato una sola volta (transazione PKCE monouso)
  const redirectInFlight = useRef<Promise<void> | null>(null);

  const handleLoginRedirect = useCallback((): Promise<void> => {
    if (!redirectInFlight.current) {
      redirectInFlight.current = completeLoginRedirect().catch((err) => {
        redirectInFlight.current = null;
        throw err;
      });
    }
    return redirectInFlight.current;
  }, []);

  const completeLoginRedirect = async (): Promise<void> => {
    try {
      // Gestisce il callback di Okta dopo il redirect
      await oktaAuth.handleLoginRedirect();
      
      // Verifica l'autenticazione
      const isAuthResult = await oktaAuth.isAuthenticated();
      setIsAuthenticated(isAuthResult);
      
      if (isAuthResult) {
        // Ottieni il token di accesso
        const token = await oktaAuth.getAccessToken();
        setAccessToken(token || null);
        
        // Setta il token nell'apiClient per le richieste API
        if (token) {
          apiClient.setToken(token);
        }
        
        // Ottieni i claim del token
        const userClaims = await oktaAuth.getUser();
        const mappedUserInfo: OktaUserInfo = {
          email: userClaims.email ?? 'email@example.com',
          name: userClaims.name ?? 'User',
          given_name: userClaims.given_name,
          family_name: userClaims.family_name,
          ...userClaims
        };
        setOktaUserInfo(mappedUserInfo);
      }
    } catch (err) {
      console.error('Login redirect error:', err);
      setError('Si è verificato un errore durante il completamento del login');
      throw err;
    }
  };

  return (
    <AuthContext.Provider
      value={{
        isAuthenticated,
        isLoading: isLoading || isLoadingUser,
        userInfo: userInfo || null,
        oktaUserInfo,
        error,
        login,
        logout,
        handleLoginRedirect,
        hasRole,
        checkIsUserInRole,
        checkHasPermission,
        checkAccess,
        isAdmin,
        isAdminUfficioStile,
        isAdminSviluppo,
        canManageRequests,
        canManageTasks
      }}
    >

      {children}
    </AuthContext.Provider>
  );
};

export const useOktaAuth = () => {
  const context = useContext(AuthContext);
  
  if (context === undefined || context === null) {
    throw new Error('useOktaAuth must be used within an OktaAuthProvider');
  }
  
  return context;
};
